import {NgClass} from "@angular/common";
import {Component, EventEmitter, inject, OnInit} from '@angular/core';

import {ToastrService} from "ngx-toastr";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";

import {
  ProfileSaleResponse,
  DeleteGroupProfileSalesRequest,
  GenerateGroupedSalesPdfRequest
} from "@numizmat/services-models/core";
import {AdType} from "@numizmat/enumerations";
import {environment} from "@numizmat/environments";
import {ImageComponent} from "@numizmat/components";
import {ForceRepaintDirective} from "@numizmat/directives";
import {ProfileSaleApiService} from "@numizmat/services/core";

@Component({
  selector: 'nz-profile-sales-group-actions-modal',
  imports: [
    ForceRepaintDirective,
    ImageComponent,
    NgClass
  ],
  templateUrl: './profile-sales-group-actions-modal.component.html',
  styleUrl: './profile-sales-group-actions-modal.component.scss'
})
export class ProfileSalesGroupActionsModalComponent implements OnInit {
  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private toastrService: ToastrService = inject(ToastrService);
  private profileSaleApiService: ProfileSaleApiService = inject(ProfileSaleApiService);

  protected totalAmount: number = 0;
  protected mode!: 'delete' | 'group';
  protected loadingData: boolean = true;
  protected loadingSubmit: boolean = false;
  protected sales: ProfileSaleResponse[] = [];
  protected selectedSaleIds: string[] = [];
  protected reloadData: EventEmitter<boolean> = new EventEmitter();

  protected readonly AdType = AdType;
  protected readonly environment = environment;

  public ngOnInit(): void {
    this.getAllProfileSales();
  }

  protected onSubmit(): void {
    if (this.selectedSaleIds.length === 0) {
      return;
    }
    this.loadingSubmit = true;
    if (this.mode === 'group') {
      this.generateGroupedSalesPdf();
    } else if (this.mode === 'delete') {
      this.deleteGroupProfileSales();
    }
  }

  protected closeModal(): void {
    this.activeModal.dismiss();
  }

  protected toggleSelection(saleId: string): void {
    const index: number = this.selectedSaleIds.findIndex(x => x === saleId);
    if (index >= 0) {
      this.selectedSaleIds.splice(index, 1);
    } else {
      this.selectedSaleIds.push(saleId);
    }
    this.calculateTotalAmount();
  }

  protected toggleSelectAll(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.selectedSaleIds = this.sales.map(x => {
        return x.saleHistoryId
      });
    } else {
      this.selectedSaleIds = [];
    }
    this.calculateTotalAmount();
  }

  protected areAllSelected(): boolean {
    return this.sales.length !== 0 && this.selectedSaleIds.length === this.sales.length;
  }

  private calculateTotalAmount(): void {
    this.totalAmount = this.sales
      .filter(sale => this.selectedSaleIds.includes(sale.saleHistoryId))
      .reduce((sum, sale) => sum + sale.adPrice, 0);
  }

  private getAllProfileSales(): void {
    this.profileSaleApiService.getAllProfileSales().subscribe({
      next: response => {
        this.loadingData = false;
        this.sales = response;
      }
    });
  }

  private deleteGroupProfileSales(): void {
    const request: DeleteGroupProfileSalesRequest = {
      saleHistoryIds: this.selectedSaleIds
    };
    this.profileSaleApiService.deleteGroupProfileSales(request).subscribe({
      next: response => {
        this.loadingSubmit = false;
        if (response) {
          this.reloadData.emit(true);
          this.toastrService.success('Успешно изтрихте продажби.');
        } else {
          this.toastrService.error('Възникна грешка.');
        }
        this.closeModal();
      }
    });
  }

  private generateGroupedSalesPdf(): void {
    const request: GenerateGroupedSalesPdfRequest = {
      saleHistoryIds: this.selectedSaleIds
    };
    this.profileSaleApiService.generateGroupedSalesPdf(request)
      .subscribe(blob => {
        const fileName = `grouped-sales-${new Date().toISOString().slice(0, 10)}.pdf`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
        this.closeModal();
      });
  }
}
